import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import EmptyState from "@/components/base/EmptyState";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdminAuditLog, type AdminAuditEntry } from "@/services/adminApi";

const entityTabs = [
  { value: "all", label: "All" },
  { value: "listing", label: "Listings" },
  { value: "review", label: "Reviews" },
];

const ACTION_META: Record<string, { icon: string; verb: string; chip: string }> = {
  approved: { icon: "ri-checkbox-circle-line", verb: "approved", chip: "bg-accent-100 text-accent-900" },
  rejected: { icon: "ri-close-circle-line", verb: "rejected", chip: "bg-secondary-100 text-secondary-900" },
  deleted: { icon: "ri-delete-bin-line", verb: "deleted", chip: "bg-secondary-100 text-secondary-900" },
  suspended: { icon: "ri-pause-circle-line", verb: "suspended", chip: "bg-background-200 text-foreground-800" },
  archived: { icon: "ri-archive-2-line", verb: "archived", chip: "bg-background-200 text-foreground-800" },
  featured: { icon: "ri-vip-crown-line", verb: "featured", chip: "bg-primary-100 text-primary-900" },
  replied: { icon: "ri-reply-line", verb: "replied to", chip: "bg-primary-100 text-primary-900" },
};

const metaOf = (action: string) =>
  ACTION_META[action] ?? { icon: "ri-record-circle-line", verb: action, chip: "bg-background-200 text-foreground-800" };

export default function AdminActivityLog() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdminAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [entity, setEntity] = useState("all");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminAuditLog(token)
      .then((log) => active && setRows(log))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesEntity = entity === "all" || r.entityType === entity;
      const matchesQuery =
        !q ||
        r.entityLabel.toLowerCase().includes(q) ||
        r.actor.toLowerCase().includes(q) ||
        r.action.toLowerCase().includes(q);
      return matchesEntity && matchesQuery;
    });
  }, [rows, query, entity]);

  return (
    <>
      <Seo title="Activity Log" path="/admin/activity" />

      <AdminPageHeader
        icon="ri-history-line"
        title="Activity log"
        subtitle="An audit trail of who approved, rejected or deleted each listing and review."
        actions={
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i> Refresh
          </button>
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search by listing, reviewer or action…">
          <AdminFilterTabs options={entityTabs} value={entity} onChange={setEntity} />
        </AdminToolbar>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            ))}
          </div>
        ) : error ? (
          <div className="space-y-4">
            <EmptyState
              icon="ri-wifi-off-line"
              title="Couldn't load the activity log"
              description="Please refresh and try again."
            />
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i> Retry
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-history-line"
            title="No activity recorded yet"
            description="Approvals, rejections and deletions of listings and reviews will appear here."
          />
        ) : (
          <ol className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {filtered.map((entry) => {
              const meta = metaOf(entry.action);
              return (
                <li key={entry.id} className="flex items-start gap-3 border-b border-background-100 px-4 py-3.5 last:border-b-0 hover:bg-background-100/40">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-100 text-foreground-700">
                    <i className={`${meta.icon} text-lg`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground-800">
                      <span className="font-semibold text-foreground-950">{entry.actor}</span>{" "}
                      <span className="font-semibold text-primary-700">{meta.verb}</span>{" "}
                      <span className="font-medium">{entry.entityLabel || (entry.entityType === "review" ? "a review" : "a listing")}</span>
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${meta.chip}`}>
                        {entry.entityType === "review" ? "Review" : "Listing"}
                      </span>
                      {entry.note && <span className="truncate">{entry.note}</span>}
                      {!entry.note && <span>{entry.createdAt}</span>}
                      {entry.note && <span>· {entry.createdAt}</span>}
                    </div>
                  </div>
                  <Link
                    to={entry.entityType === "review" ? "/admin/reviews" : "/admin/businesses"}
                    className="mt-1 hidden shrink-0 items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800 sm:inline-flex cursor-pointer"
                  >
                    View <i className="ri-arrow-right-line"></i>
                  </Link>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </>
  );
}